import React, { useState, useContext } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { homePathForRole } from "../../ProtectedRoute";
import AuthContext from "../../../contexts/AuthContext/AuthContext";
import PasswordInput from "../../common/PasswordInput";
import "../Auth.css";

// Function to decode JWT
const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return {};
  }
};

// Where a player lands after signing in: a temporary password is replaced
// first, then a provider's request waiting for them is shown. (An invite link
// signs people in on the /join page itself.)
export const afterSignInPath = (user) => {
  if (user.role === "clinician") return homePathForRole("clinician");
  if (user.must_change_password) return "/settings";
  if (user.care_requests_pending > 0) return "/settings/care-team";
  return "/home";
};

const SignInPage = () => {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isForgotUsername, setIsForgotUsername] = useState(false);
  const [emailForReset, setEmailForReset] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [isRegisterPrompt, setIsRegisterPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const response = await axios.post(`${SERVER_URL}/api/auth/jwt/create/`, {
        username: userName,
        password: password,
      });
      const { access, refresh } = response.data;
      const decodedToken = decodeJWT(access);
      let userData = {
        username: userName,
        id: decodedToken.user_id || decodedToken.sub || null,
      };
      // Fetch the full profile (incl. role) so we land on the right home.
      try {
        const me = await axios.get(`${SERVER_URL}/api/current-user/`, {
          headers: { Authorization: `Bearer ${access}` },
        });
        userData = me.data;
      } catch (meErr) {
        console.error("Could not fetch current user after login:", meErr);
      }
      login(userData, access, refresh);
      navigate(afterSignInPath(userData));
    } catch (err) {
      setError(
        err.response?.data?.detail || "Invalid credentials. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage("");
    setError("");
    setIsLoading(true);
    try {
      await axios.post(`${SERVER_URL}/auth/users/reset_password/`, {
        email: emailForReset,
      });
      setResetMessage(
        "Password reset email sent. Check your inbox — if it's not there within a " +
          "few minutes, look in your junk or spam folder. It's from support@mendgrove.com."
      );
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Email not registered.");
        setIsRegisterPrompt(true);
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotUsername = async (e) => {
    e.preventDefault();
    setResetMessage("");
    setError("");
    setIsLoading(true);
    try {
      await axios.post(`${SERVER_URL}/api/forgot-username/`, {
        email: emailForReset,
      });
      setResetMessage(
        "Username reminder email sent. Check your inbox — if it's not there within a " +
          "few minutes, look in your junk or spam folder. It's from support@mendgrove.com."
      );
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Email not registered.");
        setIsRegisterPrompt(true);
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Sign In – Mend Grove</title>
        <meta
          name="description"
          content="Sign in to your Mend Grove account to continue your brain-training exercises."
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://mendgrove.com/signin" />
        <meta property="og:title" content="Sign In – Mend Grove" />
        <meta
          property="og:description"
          content="Sign in to your Mend Grove account to continue your brain-training exercises."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mendgrove.com/signin" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sign In – Mend Grove" />
        <meta
          name="twitter:description"
          content="Sign in to your Mend Grove account to continue your brain-training exercises."
        />
        <meta name="twitter:image" content="https://mendgrove.com/logo192.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Mend Grove",
              "url": "https://mendgrove.com/",
              "logo": "https://mendgrove.com/logo192.png",
              "sameAs": [
                "https://www.linkedin.com/company/mendgrove",
                "https://www.facebook.com/mendgrove"
              ],
              "description": "Mend Grove offers gentle, science-based brain-training games to support cognitive recovery and everyday mental exercise.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@mendgrove.com",
                "contactType": "customer support",
                "url": "https://mendgrove.com/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <div className="auth-card">
        {!isForgotPassword && !isForgotUsername ? (
          <>
            <h2 className="auth-title">
              Sign In
            </h2>
            <form onSubmit={handleLogin}>
              <div className="auth-field">
                <label
                  htmlFor="username"
                  className="auth-label"
                >
                  Username or email
                </label>
                <input
                  type="text"
                  id="username"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="auth-input"
                  required
                  autoFocus
                  autoComplete="username"
                  autoCapitalize="none"
                />
              </div>
              <div className="auth-field">
                <label
                  htmlFor="password"
                  className="auth-label"
                >
                  Password
                </label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <div className="auth-inline-actions">
                  <button
                    type="button"
                    onClick={() => setIsForgotUsername(true)}
                    className="auth-link-button"
                  >
                    Forgot Username?
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsForgotPassword(true)}
                    className="auth-link-button"
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>
              {error && (
                <p
                  className="auth-message auth-message--error"
                  aria-live="polite"
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                className="auth-submit"
                disabled={isLoading}
              >
                {isLoading ? "Signing In..." : "Sign In"}
              </button>
            </form>
            <div className="auth-footer">
              Not registered?{" "}
              <Link to="/register" className="auth-link">
                Register here
              </Link>
            </div>
            <div className="auth-footer">
              Healthcare provider?{" "}
              <Link to="/provider/signin" className="auth-link">
                Sign in to the provider portal
              </Link>
            </div>
          </>
        ) : isForgotPassword ? (
          <>
            <h2 className="auth-title">
              Reset Password
            </h2>
            <form onSubmit={handleForgotPassword}>
              <div className="auth-field">
                <label
                  htmlFor="email"
                  className="auth-label"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={emailForReset}
                  onChange={(e) => setEmailForReset(e.target.value)}
                  className="auth-input"
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>
              {resetMessage && (
                <p
                  className="auth-message auth-message--success"
                  aria-live="polite"
                >
                  {resetMessage}
                </p>
              )}
              {error && (
                <p
                  className="auth-message auth-message--error"
                  aria-live="polite"
                >
                  {error}
                </p>
              )}
              {isRegisterPrompt && (
                <p className="auth-message auth-message--info">
                  Email not found.{" "}
                  <Link to="/register" className="auth-link">
                    Register here
                  </Link>
                </p>
              )}
              <button
                type="submit"
                className="auth-submit"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Reset Email"}
              </button>
            </form>
            <div className="auth-footer">
              <button
                onClick={() => setIsForgotPassword(false)}
                className="auth-link-button"
              >
                Back to Sign In
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="auth-title">
              Retrieve Username
            </h2>
            <form onSubmit={handleForgotUsername}>
              <div className="auth-field">
                <label
                  htmlFor="email"
                  className="auth-label"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={emailForReset}
                  onChange={(e) => setEmailForReset(e.target.value)}
                  className="auth-input"
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>
              {resetMessage && (
                <p
                  className="auth-message auth-message--success"
                  aria-live="polite"
                >
                  {resetMessage}
                </p>
              )}
              {error && (
                <p
                  className="auth-message auth-message--error"
                  aria-live="polite"
                >
                  {error}
                </p>
              )}
              {isRegisterPrompt && (
                <p className="auth-message auth-message--info">
                  Email not found.{" "}
                  <Link to="/register" className="auth-link">
                    Register here
                  </Link>
                </p>
              )}
              <button
                type="submit"
                className="auth-submit"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Username Email"}
              </button>
            </form>
            <div className="auth-footer">
              <button
                onClick={() => setIsForgotUsername(false)}
                className="auth-link-button"
              >
                Back to Sign In
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SignInPage;
